import { useControllableState } from "@faire/slate/private/hooks/useControllableState";
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  type Placement,
} from "@floating-ui/react";
import { useRef, useMemo } from "react";

import {
  POPOVER_FLIP_DETECTION_OFFSET,
  POPOVER_OFFSET,
  POPOVER_ARROW_OFFSET,
} from "./consts";
import type {
  IPopoverOptions,
  PopoverAlignment,
  PopoverPosition,
} from "./types";

export function usePopoverContextValue({
  alignment,
  initiallyOpen,
  open: controlledOpen,
  position,
  role: roleProp,
  onOpenChange: onOpenChangeProp,
}: IPopoverOptions) {
  const arrowRef = useRef<SVGSVGElement>(null);

  const [open, setOpen] = useControllableState({
    initialValue: initiallyOpen,
    value: controlledOpen,
    setValue: onOpenChangeProp,
  });

  const data = useFloating({
    placement: getPlacement(position, alignment),
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({ mainAxis: POPOVER_OFFSET, alignmentAxis: POPOVER_ARROW_OFFSET }),
      flip({
        fallbackAxisSideDirection: "start",
        padding: POPOVER_FLIP_DETECTION_OFFSET,
      }),
      arrow({ element: arrowRef }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    move: false,
    enabled: controlledOpen == null,
  });
  const focus = useFocus(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: roleProp });

  const interactions = useInteractions([hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      arrowRef,
      open,
      setOpen,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data]
  );
}

const getPlacement = (
  position: PopoverPosition,
  alignment: PopoverAlignment
): Placement => {
  if (alignment === "middle") {
    return position;
  }

  return `${position}-${alignment}`;
};
